import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  contactsApi,
  companiesApi,
  notesApi,
  attachmentsApi,
  pipelineApi,
  dealsApi,
  settingsApi,
  dashboardApi,
  customFieldsApi,
  customObjectsApi,
  objectRecordsApi,
  packsApi,
  type DealsSearchParams,
} from '../utils/api';
import { queryKeys } from '../lib/queryClient';
import { CRM_COPY } from '../lib/crm-copy';
import { useAuth } from '../contexts/AuthContext';
import type {
  SearchParams,
  ContactFormData,
  NoteFormData,
  OrgModuleSettings,
  OrgRoleDefault,
  ApiResponse,
  Contact,
  ContactsResponse,
  PipelineBoard,
  Pipeline,
  Stage,
  Deal,
  CustomFieldDefinitionInput,
  CustomFieldObjectType,
  CustomFieldChanges,
  CustomObjectInput,
  ObjectRecordsParams,
} from '../types/api';
import { useToast } from './useToast';

const MESSAGES = CRM_COPY.mutationMessages;
type CompaniesApiResponse = Awaited<ReturnType<typeof companiesApi.getCompanies>>;

// Shape of a single stage column on the deals board (mirrors dealsApi.getDealsBoard's
// envelope `data`). Defined locally because api.ts inlines the return type rather than
// exporting a named one. `deals: Deal[]` replaces the untyped `any[]`.
interface DealsBoardStage {
  stage: Stage;
  deals: Deal[];
  count: number;
  totalCount: number;
  totalValue: number;
  hasMore: boolean;
}

interface DealsBoard {
  pipeline: Pipeline;
  stages: DealsBoardStage[];
  totalDeals: number;
  totalValue: number;
}

// ====================================
// CONTACTS QUERIES
// ====================================

export function useContactsQuery(params: SearchParams = {}) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.contacts.list(orgSlug, params),
    queryFn: () => contactsApi.getContacts(params),
    select: data => {
      // Force new object references to trigger React re-renders
      return {
        ...data.data,
        contacts: data.data?.contacts?.map((contact: Contact) => ({ ...contact })) || [],
        _timestamp: Date.now(), // Force reference change
      };
    }, // Extract the actual data from ApiResponse
    enabled: !!orgSlug, // Only run query if user has organization context
    // Optimize for performance and cold starts
    staleTime: 0, // Always consider data stale for immediate updates
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    placeholderData: previousData => previousData, // Keep previous data while loading
    retry: 1, // Reduce retries for faster failure
    retryDelay: 500, // Faster retry
    networkMode: 'online', // Skip if offline
  });

  // Prefetch next page for better UX
  const currentPage = params.page || 1;
  const hasNextPage = query.data?.pagination?.hasNextPage;

  if (hasNextPage && !params.search) {
    // Only prefetch on non-search queries
    const nextPageParams = { ...params, page: currentPage + 1 };
    queryClient.prefetchQuery({
      queryKey: queryKeys.contacts.list(orgSlug, nextPageParams),
      queryFn: () => contactsApi.getContacts(nextPageParams),
      staleTime: 5 * 60 * 1000, // Shorter stale time for prefetched data
    });
  }

  return query;
}

export function useContactQuery(contactId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.contacts.detail(orgSlug, contactId || ''),
    queryFn: () => contactsApi.getContact(contactId ?? ''),
    enabled: !!contactId && !!orgSlug, // Only run if contactId exists and user has organization context
    select: data => data.data,
  });
}

// ====================================
// CONTACT MUTATIONS
// ====================================

export function useCreateContactMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (data: ContactFormData) => contactsApi.createContact(data),
    onSuccess: response => {
      if (response.success) {
        // Invalidate contacts list to refetch with new contact
        queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all(orgSlug), exact: false });
        showSuccess(MESSAGES.contacts.created);
      }
    },
    onError: error => {
      const message = error?.message || MESSAGES.contacts.createFailed;
      showError(message);
    },
  });
}

export function useUpdateContactMutation(contactId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (data: Partial<ContactFormData>) => contactsApi.updateContact(contactId, data),
    onSuccess: response => {
      if (response.success && response.data) {
        // 1. Optimistic update: immediately update the contact in all relevant caches
        const updatedContact = response.data;

        // Update detail cache
        queryClient.setQueryData(queryKeys.contacts.detail(orgSlug, contactId), response);

        // 1b. Immediately update ALL cached contacts list queries so cards update instantly.
        // The cache holds the raw queryFn result (ApiResponse<ContactsResponse>) — i.e. the
        // pre-`select` envelope — so contacts live at `old.data.contacts`, not `old.contacts`.
        queryClient.setQueriesData(
          { queryKey: queryKeys.contacts.lists(orgSlug), exact: false },
          (old: ApiResponse<ContactsResponse> | undefined) => {
            if (!old?.data || !Array.isArray(old.data.contacts)) return old;

            const newContacts = old.data.contacts.map((c: Contact) =>
              c.id === updatedContact.id ? { ...c, ...updatedContact } : c
            );
            return { ...old, data: { ...old.data, contacts: newContacts } };
          }
        );

        // 2. Invalidate all contact list queries to refetch fresh data in background
        queryClient.invalidateQueries({
          queryKey: queryKeys.contacts.all(orgSlug),
          exact: false,
        });

        // 3. Invalidate pipeline queries since contacts might be in pipeline
        queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all(orgSlug) });
        showSuccess(MESSAGES.contacts.updated);
      }
    },
    onError: error => {
      const message = error?.message || MESSAGES.contacts.updateFailed;
      showError(message);
    },
  });
}

export function useDeleteContactMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async (contactId: string) => {
      const response = await contactsApi.deleteContact(contactId);
      if (!response.success) {
        throw new Error(response.error || MESSAGES.contacts.deleteFailed);
      }
      return response;
    },
    onSuccess: (_, contactId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.contacts.detail(orgSlug, contactId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all(orgSlug), exact: false });
      showSuccess(MESSAGES.contacts.deleted);
    },
    onError: error => {
      const message = error?.message || MESSAGES.contacts.deleteFailed;
      showError(message);
    },
  });
}

export function useCoreDashboardQuery() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['dashboard', user?.orgSlug, 'core'],
    queryFn: () => dashboardApi.getCoreDashboard(),
    enabled: !!user?.orgSlug,
    select: data => data.data,
    staleTime: 30_000,
  });
}

const CUSTOM_FIELD_MESSAGES = CRM_COPY.customFields.messages;

export function useCustomFieldsQuery(objectType: CustomFieldObjectType) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.customFields.list(orgSlug, objectType),
    queryFn: async () => {
      const response = await customFieldsApi.listCustomFields(objectType);
      if (!response.success || !response.data) {
        throw new Error(response.error || CRM_COPY.customFields.section.loadFailed);
      }
      return response.data.custom_fields;
    },
    enabled: !!orgSlug,
    staleTime: 60_000,
  });
}

export function useSaveCustomFieldMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: CustomFieldDefinitionInput }) => {
      const response = id
        ? await customFieldsApi.updateCustomField(id, payload)
        : await customFieldsApi.createCustomField(payload);
      if (!response.success || !response.data) {
        throw new Error(response.error || CUSTOM_FIELD_MESSAGES.saveFailed);
      }
      return { created: !id, field: response.data.custom_field };
    },
    onSuccess: ({ created }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.all(user?.orgSlug) });
      showSuccess(created ? CUSTOM_FIELD_MESSAGES.created : CUSTOM_FIELD_MESSAGES.updated);
    },
    onError: (error: Error) => showError(error.message || CUSTOM_FIELD_MESSAGES.saveFailed),
  });
}

export function useDeleteCustomFieldMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await customFieldsApi.deleteCustomField(id);
      if (!response.success) {
        throw new Error(response.error || CUSTOM_FIELD_MESSAGES.deleteFailed);
      }
    },
    onSuccess: () => {
      // Values are erased from records too, so record caches are stale.
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.all(user?.orgSlug) });
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all(user?.orgSlug) });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all(user?.orgSlug) });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      showSuccess(CUSTOM_FIELD_MESSAGES.deleted);
    },
    onError: (error: Error) => showError(error.message || CUSTOM_FIELD_MESSAGES.deleteFailed),
  });
}

const OBJECT_MESSAGES = CRM_COPY.customObjects.messages;
const RECORD_MESSAGES = CRM_COPY.customObjects.records;

export function useCustomObjectsQuery() {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.customObjects.all(orgSlug),
    queryFn: async () => {
      const response = await customObjectsApi.listCustomObjects();
      if (!response.success || !response.data) {
        throw new Error(response.error || CRM_COPY.customObjects.section.loadFailed);
      }
      return response.data.custom_objects;
    },
    enabled: !!orgSlug,
    staleTime: 60_000,
  });
}

export interface ObjectCatalogEntry {
  key: string;
  labelSingular: string;
  labelPlural: string;
  core: boolean;
}

/** Every object usable in this workspace: the core objects, then custom objects. */
export function useObjectCatalog(): ObjectCatalogEntry[] {
  const customObjects = useCustomObjectsQuery().data || [];
  const coreLabels = CRM_COPY.customFields.objects;
  return [
    ...(['contacts', 'companies', 'deals'] as const).map(key => ({
      key,
      labelSingular: coreLabels[key],
      labelPlural: coreLabels[key],
      core: true,
    })),
    ...customObjects.map(object => ({
      key: object.key,
      labelSingular: object.label_singular,
      labelPlural: object.label_plural,
      core: false,
    })),
  ];
}

export function useSaveCustomObjectMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async ({ key, payload }: { key?: string; payload: CustomObjectInput }) => {
      const response = key
        ? await customObjectsApi.updateCustomObject(key, payload)
        : await customObjectsApi.createCustomObject(payload);
      if (!response.success || !response.data) {
        throw new Error(response.error || OBJECT_MESSAGES.saveFailed);
      }
      return { created: !key, object: response.data.custom_object };
    },
    onSuccess: ({ created }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customObjects.all(user?.orgSlug) });
      showSuccess(created ? OBJECT_MESSAGES.created : OBJECT_MESSAGES.updated);
    },
    onError: (error: Error) => showError(error.message || OBJECT_MESSAGES.saveFailed),
  });
}

export function useDeleteCustomObjectMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async (key: string) => {
      const response = await customObjectsApi.deleteCustomObject(key);
      if (!response.success) throw new Error(response.error || OBJECT_MESSAGES.deleteFailed);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customObjects.all(user?.orgSlug) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.all(user?.orgSlug) });
      showSuccess(OBJECT_MESSAGES.deleted);
    },
    onError: (error: Error) => showError(error.message || OBJECT_MESSAGES.deleteFailed),
  });
}

export function useObjectRecordsQuery(objectKey: string, params: ObjectRecordsParams) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: [...queryKeys.customObjects.records(orgSlug, objectKey), params],
    queryFn: async () => {
      const response = await objectRecordsApi.listRecords(objectKey, params);
      if (!response.success || !response.data) {
        throw new Error(response.error || RECORD_MESSAGES.loadFailed);
      }
      return response.data;
    },
    enabled: !!orgSlug && !!objectKey,
    placeholderData: previous => previous,
  });
}

export function useObjectRecordQuery(objectKey: string, id: string | undefined) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.customObjects.record(orgSlug, objectKey, id || ''),
    queryFn: async () => {
      const response = await objectRecordsApi.getRecord(objectKey, id as string);
      if (!response.success || !response.data) {
        throw new Error(response.error || RECORD_MESSAGES.recordNotFound);
      }
      return response.data.record;
    },
    enabled: !!orgSlug && !!objectKey && !!id,
  });
}

export function useSaveObjectRecordMutation(objectKey: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id?: string;
      payload: { name?: string; custom_fields?: CustomFieldChanges };
    }) => {
      const response = id
        ? await objectRecordsApi.updateRecord(objectKey, id, payload)
        : await objectRecordsApi.createRecord(objectKey, { name: payload.name || '', ...payload });
      if (!response.success || !response.data) {
        throw new Error(response.error || RECORD_MESSAGES.saveFailed);
      }
      return { created: !id, record: response.data.record };
    },
    onSuccess: ({ created }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customObjects.records(user?.orgSlug, objectKey),
      });
      showSuccess(created ? RECORD_MESSAGES.created : RECORD_MESSAGES.updated);
    },
    onError: (error: Error) => showError(error.message || RECORD_MESSAGES.saveFailed),
  });
}

export function useDeleteObjectRecordMutation(objectKey: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await objectRecordsApi.deleteRecord(objectKey, id);
      if (!response.success) throw new Error(response.error || RECORD_MESSAGES.deleteFailed);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customObjects.records(user?.orgSlug, objectKey),
      });
      showSuccess(RECORD_MESSAGES.deleted);
    },
    onError: (error: Error) => showError(error.message || RECORD_MESSAGES.deleteFailed),
  });
}

const PACK_MESSAGES = CRM_COPY.packs.messages;

export function usePacksQuery() {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.packs.all(orgSlug),
    queryFn: async () => {
      const response = await packsApi.listPacks();
      if (!response.success || !response.data) {
        throw new Error(response.error || CRM_COPY.packs.section.loadFailed);
      }
      return response.data;
    },
    enabled: !!orgSlug,
  });
}

export type PackAction =
  | { action: 'install'; key: string; includeSampleData: boolean }
  | { action: 'upgrade'; key: string }
  | { action: 'uninstall'; key: string };

/** Install, upgrade or uninstall a pack, then refresh everything a pack can change. */
export function usePackActionMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: async (request: PackAction) => {
      const response =
        request.action === 'install'
          ? await packsApi.installPack(request.key, {
              include_sample_data: request.includeSampleData,
            })
          : request.action === 'upgrade'
            ? await packsApi.upgradePack(request.key)
            : await packsApi.uninstallPack(request.key, { delete_data: true });
      if (!response.success) throw new Error(response.error || PACK_MESSAGES.failed);
      return request.action;
    },
    onSuccess: action => {
      for (const queryKey of [
        queryKeys.packs.all(orgSlug),
        queryKeys.customObjects.all(orgSlug),
        queryKeys.customFields.all(orgSlug),
        queryKeys.pipeline.all(orgSlug),
        ['object-records', orgSlug],
        ['deals', orgSlug],
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
      showSuccess(
        action === 'install'
          ? PACK_MESSAGES.installed
          : action === 'upgrade'
            ? PACK_MESSAGES.upgraded
            : PACK_MESSAGES.uninstalled
      );
    },
    onError: (error: Error) => showError(error.message || PACK_MESSAGES.failed),
  });
}

export function useOrgSettingsQuery(options: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.settings.org(orgSlug),
    queryFn: () => settingsApi.getOrgSettings(),
    enabled: !!orgSlug && (options.enabled ?? true),
    select: data => data.data,
  });
}

export function useUpdateOrgSettingsMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (payload: {
      module_settings: OrgModuleSettings;
      role_defaults?: OrgRoleDefault[];
    }) => settingsApi.updateOrgSettings(payload),
    onSuccess: response => {
      if (!response.success || !response.data) {
        showError(response.error || MESSAGES.settings.updateFailed);
        return;
      }

      queryClient.setQueryData(queryKeys.settings.org(orgSlug), {
        success: true,
        data: response.data,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all(orgSlug), exact: false });
      showSuccess(MESSAGES.settings.updated);
    },
    onError: error => {
      showError(error?.message || MESSAGES.settings.updateFailed);
    },
  });
}

// ====================================
// COMPANIES QUERIES
// ====================================

export function useCompaniesQuery(
  params: { search?: string; page?: number; limit?: number } = {},
  options: Omit<
    UseQueryOptions<CompaniesApiResponse, Error, CompaniesApiResponse['data']>,
    'queryKey' | 'queryFn' | 'select'
  > = {}
) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  // Ensure enabled is always a boolean
  const baseEnabled = !!orgSlug;
  const optionsEnabled = typeof options.enabled === 'boolean' ? options.enabled : true;
  const finalEnabled = baseEnabled && optionsEnabled;

  return useQuery<CompaniesApiResponse, Error, CompaniesApiResponse['data']>({
    queryKey: queryKeys.companies.list(orgSlug, params),
    queryFn: async () => {
      const response = await companiesApi.getCompanies(params);
      if (!response.success) {
        throw new Error(response.error || MESSAGES.companies.loadFailed);
      }
      return response;
    },
    select: data => data.data,
    ...options,
    enabled: finalEnabled, // Override with our combined boolean logic
  });
}

export function useCompanyQuery(companyId: string, options = {}) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.companies.detail(orgSlug, companyId),
    queryFn: () => companiesApi.getCompany(companyId),
    enabled: !!companyId && !!orgSlug,
    ...options,
  });
}

// ====================================
// NOTES QUERIES
// ====================================

export function useNotesQuery(contactId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.notes.byContact(orgSlug, contactId || ''),
    queryFn: () => notesApi.getNotes(contactId ?? ''),
    enabled: !!contactId && !!orgSlug,
    select: data => data.data?.notes || [],
  });
}

export function useCreateNoteMutation(contactId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (data: NoteFormData) => notesApi.createNote(contactId, data),
    onSuccess: () => {
      // Invalidate notes for this contact
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byContact(orgSlug, contactId),
      });
      showSuccess(MESSAGES.notes.added);
    },
    onError: error => {
      const message = error?.message || MESSAGES.notes.addFailed;
      showError(message);
    },
  });
}

export function useDeleteNoteMutation(contactId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (noteId: string) => notesApi.deleteNote(contactId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byContact(orgSlug, contactId),
      });
      showSuccess(MESSAGES.notes.deleted);
    },
    onError: error => {
      const message = error?.message || MESSAGES.notes.deleteFailed;
      showError(message);
    },
  });
}

// ====================================
// ATTACHMENTS QUERIES
// ====================================

export function useAttachmentsQuery(contactId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.attachments.byContact(orgSlug, contactId || ''),
    queryFn: () => attachmentsApi.getAttachments(contactId ?? ''),
    enabled: !!contactId && !!orgSlug,
    select: data => data.data?.attachments || [],
  });
}

export function useUploadAttachmentMutation(contactId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (file: File) => attachmentsApi.uploadAttachment(contactId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.byContact(orgSlug, contactId),
      });
      showSuccess(MESSAGES.attachments.uploaded);
    },
    onError: error => {
      const message = error?.message || MESSAGES.attachments.uploadFailed;
      showError(message);
    },
  });
}

export function useDeleteAttachmentMutation(contactId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.deleteAttachment(contactId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.byContact(orgSlug, contactId),
      });
      showSuccess(MESSAGES.attachments.deleted);
    },
    onError: error => {
      const message = error?.message || MESSAGES.attachments.deleteFailed;
      showError(message);
    },
  });
}

// ====================================
// PIPELINE QUERIES
// ====================================

export function usePipelineStructureQuery(pipelineId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.pipeline.board(orgSlug, pipelineId || '', 'structure'),
    queryFn: () => pipelineApi.getPipelineStructure(pipelineId ?? ''),
    enabled: !!pipelineId && !!orgSlug,
    select: data => data.data,
    // Very fast loading for structure
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: 300,
  });
}

export function usePipelineBoardQuery(pipelineId: string | null, page = 1, limit = 50) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.pipeline.board(orgSlug, pipelineId || '', page, limit),
    queryFn: () => pipelineApi.getPipelineBoard(pipelineId ?? '', page, limit),
    enabled: !!pipelineId && !!orgSlug,
    select: data => data.data,
    // Optimize for pipeline performance
    staleTime: 3 * 60 * 1000, // 3 minutes - matches backend cache
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: previousData => previousData, // Keep previous data while loading
    retry: 1, // Reduce retries for faster failure
    retryDelay: 500,
  });
}

// Infinite scroll version for large pipelines
export function usePipelineBoardInfiniteQuery(pipelineId: string | null, limit = 50) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useInfiniteQuery({
    queryKey: queryKeys.pipeline.board(orgSlug, pipelineId || '', 'infinite', limit),
    queryFn: ({ pageParam = 1 }) =>
      pipelineApi.getPipelineBoard(pipelineId ?? '', pageParam as number, limit),
    enabled: !!pipelineId && !!orgSlug,
    getNextPageParam: (lastPage, allPages) => {
      // Check if any stage has more data
      const hasMore = lastPage.data?.stages.some(stage => stage.hasMore) || false;
      return hasMore ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    select: data => {
      // Merge all pages into a single board
      if (!data.pages.length) return null;

      const firstPage = data.pages[0].data;
      if (!firstPage) return null;

      // Combine contacts from all pages by stage
      const mergedStages = firstPage.stages.map((stage, stageIndex) => {
        const allContactsForStage: Contact[] = [];
        let totalCount = stage.totalCount;
        let hasMore = false;

        // Collect contacts from all pages for this stage
        data.pages.forEach(page => {
          if (page.data?.stages[stageIndex]) {
            allContactsForStage.push(...page.data.stages[stageIndex].contacts);
            totalCount = page.data.stages[stageIndex].totalCount;
            hasMore = page.data.stages[stageIndex].hasMore;
          }
        });

        return {
          ...stage,
          contacts: allContactsForStage,
          count: allContactsForStage.length,
          totalCount,
          hasMore,
        };
      });

      return {
        ...firstPage,
        stages: mergedStages,
      };
    },
    // Performance optimizations
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    retryDelay: 500,
  });
}

export function usePipelineHistoryQuery(contactId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: queryKeys.pipeline.history(orgSlug, contactId || ''),
    queryFn: () => pipelineApi.getPipelineHistory(contactId ?? ''),
    enabled: !!contactId && !!orgSlug,
    select: data => data.data?.history || [],
  });
}

export function useMoveContactMutation(pipelineId?: string | null) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ contactId, stageId }: { contactId: string; stageId: string }) =>
      pipelineApi.moveContact(contactId, stageId),
    onSuccess: (_response, variables) => {
      // Optimistically update the cached board to prevent snap-back
      if (pipelineId && orgSlug) {
        const boardKey = queryKeys.pipeline.board(orgSlug, pipelineId, 1, 50);
        // The cache holds the raw queryFn result (ApiResponse<PipelineBoard>) — i.e. the
        // pre-`select` envelope — so stages live at `old.data.stages`, not `old.stages`.
        queryClient.setQueryData(boardKey, (old: ApiResponse<PipelineBoard> | undefined) => {
          if (!old?.data) return old;

          const { contactId, stageId } = variables as { contactId: string; stageId: string };
          const board = old.data;

          // Find the moved contact details from the old board
          const movedContact = board.stages.flatMap(s => s.contacts).find(c => c.id === contactId);

          const newStages = board.stages.map(stageEntry => {
            // Remove the contact from every stage
            const filtered = stageEntry.contacts.filter(c => c.id !== contactId);

            // If this is the target stage, insert at top and update stage fields
            if (stageEntry.stage.id === stageId && movedContact) {
              const updatedMoved = { ...movedContact, stage_id: stageId, stageId };
              return {
                ...stageEntry,
                contacts: [updatedMoved, ...filtered],
                count: filtered.length + 1,
              };
            }

            return {
              ...stageEntry,
              contacts: filtered,
              count: filtered.length,
            };
          });

          return { ...old, data: { ...board, stages: newStages } };
        });

        // Still invalidate to refetch latest server state in background
        queryClient.invalidateQueries({ queryKey: boardKey });
      }

      // Invalidate aggregated boards cache
      queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.boards(orgSlug) });
      showSuccess(MESSAGES.contacts.movedToStage);
    },
    onError: error => {
      const message = error?.message || MESSAGES.contacts.moveFailed;
      showError(message);
    },
  });
}

// ====================================
// DEALS QUERIES
// ====================================

export function useDealsQuery(params: DealsSearchParams = {}) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: ['deals', orgSlug, params],
    queryFn: () => dealsApi.getDeals(params),
    enabled: !!orgSlug,
    select: data => data.data,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    placeholderData: previousData => previousData,
    retry: 1,
    retryDelay: 500,
  });
}

export function useDealQuery(dealId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: ['deals', orgSlug, dealId],
    queryFn: () => dealsApi.getDeal(dealId ?? ''),
    enabled: !!dealId && !!orgSlug,
    select: data => data.data,
  });
}

export function useDealsBoard(pipelineId: string | null, page = 1, limit = 50) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: ['deals', 'board', orgSlug, pipelineId, page, limit],
    queryFn: () => dealsApi.getDealsBoard(pipelineId ?? '', page, limit),
    enabled: !!pipelineId && !!orgSlug,
    select: data => data.data,
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: previousData => previousData,
    retry: 1,
    retryDelay: 500,
  });
}

export function useDealHistoryQuery(dealId: string | null) {
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;

  return useQuery({
    queryKey: ['deals', 'history', orgSlug, dealId],
    queryFn: () => dealsApi.getDealHistory(dealId ?? ''),
    enabled: !!dealId && !!orgSlug,
    select: data => data.data,
  });
}

// ====================================
// DEALS MUTATIONS
// ====================================

export function useCreateDealMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (data: Parameters<typeof dealsApi.createDeal>[0]) => dealsApi.createDeal(data),
    onSuccess: response => {
      if (response.success) {
        // Invalidate deals lists and boards
        queryClient.invalidateQueries({ queryKey: ['deals', orgSlug] });
        showSuccess(MESSAGES.deals.created);
      } else {
        // Handle API errors that don't throw
        const message = response.error || MESSAGES.deals.createFailed;
        showError(message);
      }
    },
    onError: error => {
      const message = error?.message || MESSAGES.deals.createFailed;
      showError(message);
    },
  });
}

export function useUpdateDealMutation(dealId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (data: Parameters<typeof dealsApi.updateDeal>[1]) =>
      dealsApi.updateDeal(dealId, data),
    onSuccess: response => {
      if (response.success && response.data) {
        // Update deal cache with correct key
        queryClient.setQueryData(['deal', dealId], response);
        // Also update the deals list cache key if it exists
        queryClient.setQueryData(['deals', orgSlug, dealId], response);
        // Invalidate deals lists and boards
        queryClient.invalidateQueries({ queryKey: ['deals', orgSlug] });
        showSuccess(MESSAGES.deals.updated);
      } else {
        // Handle API errors that don't throw
        const message = response.error || MESSAGES.deals.updateFailed;
        showError(message);
      }
    },
    onError: error => {
      const message = error?.message || MESSAGES.deals.updateFailed;
      showError(message);
    },
  });
}

export function useDeleteDealMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (dealId: string) => dealsApi.deleteDeal(dealId),
    onSuccess: () => {
      // Invalidate deals lists and boards
      queryClient.invalidateQueries({ queryKey: ['deals', orgSlug] });
      showSuccess(MESSAGES.deals.deleted);
    },
    onError: error => {
      const message = error?.message || MESSAGES.deals.deleteFailed;
      showError(message);
    },
  });
}

export function useMoveDealMutation(pipelineId?: string | null) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const orgSlug = user?.orgSlug;
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ dealId, stageId }: { dealId: string; stageId: string }) =>
      dealsApi.moveDeal(dealId, stageId),
    onSuccess: (_response, variables) => {
      // Optimistically update deals board cache
      if (pipelineId && orgSlug) {
        const boardKey = ['deals', 'board', orgSlug, pipelineId, 1, 50] as const;
        // The cache holds the raw queryFn result (ApiResponse<DealsBoard>) — i.e. the
        // pre-`select` envelope — so stages live at `old.data.stages`, not `old.stages`.
        queryClient.setQueryData(boardKey, (old: ApiResponse<DealsBoard> | undefined) => {
          if (!old?.data) return old;

          const { dealId, stageId } = variables as { dealId: string; stageId: string };
          const board = old.data;

          const movedDeal = board.stages.flatMap(s => s.deals).find(d => d.id === dealId);

          const newStages = board.stages.map(stageEntry => {
            const filtered = stageEntry.deals.filter(d => d.id !== dealId);
            if (stageEntry.stage.id === stageId && movedDeal) {
              const updatedMoved = { ...movedDeal, stage_id: stageId };
              return {
                ...stageEntry,
                deals: [updatedMoved, ...filtered],
                count: filtered.length + 1,
              };
            }
            return {
              ...stageEntry,
              deals: filtered,
              count: filtered.length,
            };
          });

          return { ...old, data: { ...board, stages: newStages } };
        });

        // Background refetch for freshness
        queryClient.invalidateQueries({ queryKey: boardKey });
      }

      // Invalidate aggregate queries
      queryClient.invalidateQueries({ queryKey: ['deals', 'board', orgSlug] });
      queryClient.invalidateQueries({ queryKey: ['deals', orgSlug] });
      showSuccess(MESSAGES.deals.movedToStage);
    },
    onError: error => {
      const message = error?.message || MESSAGES.deals.moveFailed;
      showError(message);
    },
  });
}
